import { useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { CSVLink } from 'react-csv';
import SVG from 'react-inlinesvg';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls';
import SortDropdownHeader, {
  sortData,
} from '../../../components/common/SortDropdownHeader/SortDropdownHeader';
import { decryptedData } from '../../../../helpers';
import {
  useGetExportRevenueTransactions,
  useGetRevenueTransactions,
  useUpdateRevenueTransaction,
} from '../../../../swrHelpers/settings/revenueSettings';
import { formatCurrency } from '../../../../helpers/numberFormat';
import SearchFilters from './SearchFilters';
import { toAbsoluteUrl } from '../../../../_metronic/_helpers';
import RefundRejectModal from './RefundRejectModal';
import PaymentGatewayLogo from '../../../components/common/PaymentGatewaysLogo/PaymentGatewaysLogo';

const Transactions = () => {
  const [showRefundCancelModal, setShowRefundModal] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const { generalSettings } = useSelector((state) => state.settings);
  const { currentPermissions } = useSelector((state) => state.role);
  const methods = useForm({ mode: 'onTouched' });
  const isEncrypted = useRef(
    import.meta.env.VITE_DATABASE_ENCRYPTION === 'true',
  );
  const SHOW_MORE_LIMIT = 10;

  const REVENUE_TYPES = [
    {
      value: 'SPONSOR_SUBSCRIPTION',
      label: 'Sponsor Subscription',
    },
    {
      value: 'INVESTOR_SUBSCRIPTION',
      label: 'Investor Subscription',
    },
    {
      value: 'CAMPAIGN_LISTING_FEE',
      label: 'Campaign Listing Fee',
    },
    {
      value: 'INVESTMENT_FEE',
      label: 'Investment Fee',
    },
    {
      value: 'SECONDARY_MARKET_TRANSACTION_FEE',
      label: 'Secondary Market Fee',
    },
    {
      value: 'REPAYMENT_DISTRIBUTION_FEE',
      label: 'Repayment Distribution Fee',
    },
    {
      value: 'DIVIDEND_DISTRIBUTION_FEE',
      label: 'Dividend Distribution Fee',
    },
    {
      value: 'CAMPAIGN_LISTING_SALES_RESOURCE_FEE',
      label: 'Sales Resource Fees',
    },
    {
      value: 'CAMPAIGN_LISTING_MARKETING_RESOURCE_FEE',
      label: 'Marketing Resource Fees',
    },
  ];

  const STATUS_FILTERS = [
    {
      value: 0,
      label: 'Pending',
    },
    {
      value: 2,
      label: 'In Process',
    },
    {
      value: 4,
      label: 'Success',
    },
    {
      value: 6,
      label: 'Refunded',
    },
    {
      value: 8,
      label: 'Cancelled',
    },
    {
      value: 10,
      label: 'Refund Requested',
    },
    {
      value: 12,
      label: 'Refund Rejected',
    },
    {
      value: 10,
      label: 'Refund Requested',
    },
    {
      value: 12,
      label: 'Refund Rejected',
    },
  ];

  const transactionCSVHeaders = [
    { label: 'User Name', key: 'userName' },
    { label: 'Revenue Type', key: 'revenueType' },
    { label: 'Transaction ID', key: 'transactionNumber' },
    { label: 'Amount', key: 'amount' },
    { label: 'Earned', key: 'earned' },
    { label: 'Date & Time', key: 'createdAt' },
    { label: 'Payment Method', key: 'gatewayId' },
    { label: 'Status', key: 'status' },
  ];

  const MySwal = withReactContent(Swal);

  const [limit, setLimit] = useState(SHOW_MORE_LIMIT);
  const [query, setQuery] = useState('');

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });

  const {
    data: revenueTransactionData,
    isValidating,
    mutate: revenueTransactionDataMutate,
  } = useGetRevenueTransactions(limit, query);

  const { data: exportedRevenueTransactionData, isValidating: exportLoading } =
    useGetExportRevenueTransactions(query);

  const { trigger: refundProcessTrigger } = useUpdateRevenueTransaction();

  useEffect(() => {
    resetFilters();
  }, []);

  const resetFilters = () => {
    methods.reset({
      search: '',
      transactionNumber: '',
      minAmount: '',
      maxAmount: '',
      revenueType: null,
      status: null,
    });
    setQuery('');
  };

  const handleSort = (column, direction) => {
    setSortConfig({ column, direction });
  };

  const loadMore = () => {
    setLimit((prev) => prev + SHOW_MORE_LIMIT);
  };

  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setTransactionId(null);
  };

  const revenueType = (requestType) => {
    return (
      REVENUE_TYPES.find((type) => type?.value === requestType)?.label ||
      'Unknown'
    );
  };

  const statusType = (requestType) => {
    return (
      STATUS_FILTERS.find((type) => type?.value === requestType)?.label ||
      'Unknown'
    );
  };

  const processRefundRequest = async (processId, status) => {
    setTransactionId(processId);
    if (status === 12) {
      setShowRefundModal(true);
    } else {
      alertBox(
        'Are you sure want to approve this request ?',
        'success',
        processId,
      );
    }
  };

  const alertBox = (text, icon, processId) => {
    MySwal.fire({
      title: text,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: true,
    }).then(async (result) => {
      if (result['isConfirmed']) {
        const refundProcessPayload = {
          id: processId,
          status: 6,
          createdAt: Date.now(),
        };

        await refundProcessTrigger(refundProcessPayload);

        revenueTransactionDataMutate();
      }
    });
  };

  const onSubmit = (data) => {
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(
        ([_, value]) => value !== '' && value !== null && value !== undefined,
      ),
    );

    const params = new URLSearchParams(filteredData);
    setQuery(params.toString());
  };

  // Define custom sort functions for different columns
  const sortOptions = {
    userName: (transaction) => {
      const firstName = isEncrypted.current
        ? decryptedData(transaction?.userFirstName)
        : transaction?.userFirstName;
      const lastName = isEncrypted.current
        ? decryptedData(transaction?.userLastName)
        : transaction?.userLastName;
      return `${firstName} ${lastName}`;
    },
    revenueType: (transaction) =>
      revenueType(transaction?.revenueType || 'Unknown'),
    transactionId: (transaction) => {
      return isEncrypted.current
        ? decryptedData(transaction?.transactionNumber)
        : transaction?.transactionNumber;
    },
    amount: (transaction) => transaction?.totalAmount ?? 0,
    earned: (transaction) => {
      if (
        [
          'SPONSOR_SUBSCRIPTION',
          'INVESTOR_SUBSCRIPTION',
          'CAMPAIGN_LISTING_FEE',
          'CAMPAIGN_LISTING_MARKETING_RESOURCE_FEE',
          'CAMPAIGN_LISTING_SALES_RESOURCE_FEE',
        ].includes(transaction?.revenueType)
      ) {
        return transaction?.totalAmount ?? 0;
      } else if (transaction?.revenueType === 'INVESTMENT_FEE') {
        return transaction?.investmentFees?.amount ?? 0;
      } else {
        return transaction?.earnedFee ?? 0;
      }
    },
    createdAt: (transaction) => new Date(transaction?.createdAt),
    gatewayId: (transaction) => {
      if (transaction?.doneFromWallet === 'no') {
        return transaction?.paymentGatewayTitle?.toUpperCase() || '';
      } else if (transaction?.doneFromWallet === 'full') {
        return 'WALLET';
      } else if (transaction?.doneFromWallet === 'partial') {
        return `WALLET + ${
          transaction?.paymentGatewayTitle?.toUpperCase() || ''
        }`;
      }
      return '';
    },
    preapprovalStatus: (transaction) => transaction?.status ?? 0,
  };

  // Get sorted transaction data
  const sortedTransactions = revenueTransactionData?.docs
    ? sortData(revenueTransactionData.docs, sortConfig, sortOptions)
    : [];

  const displayLoadMore = revenueTransactionData?.displayLoadMore ?? false;

  const transactionList =
    sortedTransactions.length > 0 ? (
      sortedTransactions.map((transaction, key) => (
        <tr key={key}>
          <td>
            {`${
              isEncrypted.current
                ? decryptedData(transaction?.userFirstName)
                : transaction?.userFirstName
            } ${
              isEncrypted.current
                ? decryptedData(transaction?.userLastName)
                : transaction?.userLastName
            }`}
          </td>
          <td>{revenueType(transaction?.revenueType || 'Unknown')}</td>
          <td>
            {isEncrypted.current
              ? decryptedData(transaction?.transactionNumber)
              : transaction?.transactionNumber}
          </td>
          <td>
            {formatCurrency(
              transaction?.totalAmount ?? 0,
              generalSettings.currencySymbolSide,
              transaction?.currencySymbol,
              transaction?.currencyCode,
              generalSettings.decimalPoints,
            )}
          </td>
          <td>
            {[
              'SPONSOR_SUBSCRIPTION',
              'INVESTOR_SUBSCRIPTION',
              'CAMPAIGN_LISTING_FEE',
              'CAMPAIGN_LISTING_MARKETING_RESOURCE_FEE',
              'CAMPAIGN_LISTING_SALES_RESOURCE_FEE',
            ].includes(transaction?.revenueType)
              ? formatCurrency(
                  transaction?.totalAmount ?? 0,
                  generalSettings.currencySymbolSide,
                  transaction?.currencySymbol,
                  transaction?.currencyCode,
                  generalSettings.decimalPoints,
                )
              : transaction?.revenueType === 'INVESTMENT_FEE'
                ? formatCurrency(
                    transaction?.investmentFees?.amount ?? 0,
                    generalSettings.currencySymbolSide,
                    transaction?.currencySymbol,
                    transaction?.currencyCode,
                    generalSettings.decimalPoints,
                  )
                : formatCurrency(
                    transaction?.earnedFee ?? 0,
                    generalSettings.currencySymbolSide,
                    transaction?.currencySymbol,
                    transaction?.currencyCode,
                    generalSettings.decimalPoints,
                  )}
          </td>
          <td>
            {moment(transaction.createdAt).format(generalSettings.dateFormat) ??
              '-'}
            <br />
            {transaction.createdAt &&
              new Date(transaction.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
          </td>
          <td>
            <PaymentGatewayLogo
              gatewayName={transaction?.paymentGatewayTitle}
              doneFromWallet={transaction?.doneFromWallet}
            />
          </td>
          <td className="">
            <span
              className={`label text-nowrap label-lg gap-2 ${
                transaction.status === 4
                  ? 'label-light-success'
                  : transaction.status === 0 || transaction.status === 2
                    ? 'label-light-default'
                    : [6, 8, 12].includes(transaction.status)
                      ? 'label-light-danger'
                      : transaction.status === 10
                        ? 'label-light-dark'
                        : 'label-light-dark'
              } label-inline`}
            >
              {statusType(transaction?.status || '')}
            </span>
            {transaction?.status === 10 && (
              <p className="mt-2 mb-0">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Approve</Tooltip>}
                >
                  <button
                    onClick={() => processRefundRequest(transaction?._id, 6)}
                    className="action-btn-success me-2"
                  >
                    <span className="btn-svg">
                      <SVG
                        src={toAbsoluteUrl(
                          'media/svg/icons/Navigation/Check.svg',
                        )}
                      />
                    </span>
                  </button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Reject</Tooltip>}
                >
                  <button
                    onClick={() => processRefundRequest(transaction?._id, 12)}
                    className="action-btn-danger me-2"
                  >
                    <span className="btn-svg">
                      <SVG
                        src={toAbsoluteUrl(
                          'media/svg/icons/Navigation/Close.svg',
                        )}
                      />
                    </span>
                  </button>
                </OverlayTrigger>
              </p>
            )}
          </td>
        </tr>
      ))
    ) : (
      <tr className="text-center">
        <td colSpan={8}>
          {revenueTransactionData?.docs?.length === 0 ? (
            <span>
              <img
                height={120}
                src={toAbsoluteUrl('media/animations/nodata-animation.gif')}
                alt="no data found!"
              />
              <br />
              <span className="text-primary fs-6 fw-bold">
                No Data Available!
              </span>
            </span>
          ) : (
            'Loading...'
          )}
        </td>
      </tr>
    );

  const exportTransactionList =
    exportedRevenueTransactionData?.docs?.length > 0
      ? exportedRevenueTransactionData?.docs?.map((transaction) => ({
          userName: `${
            isEncrypted.current
              ? decryptedData(transaction?.userFirstName)
              : transaction?.userFirstName
          } ${
            isEncrypted.current
              ? decryptedData(transaction?.userLastName)
              : transaction?.userLastName
          }`,
          revenueType: revenueType(transaction?.revenueType || 'Unknown'),
          transactionNumber: isEncrypted.current
            ? decryptedData(transaction?.transactionNumber)
            : transaction?.transactionNumber,
          amount: formatCurrency(
            transaction?.totalAmount ?? 0,
            generalSettings.currencySymbolSide,
            transaction?.currencySymbol,
            transaction?.currencyCode,
            generalSettings.decimalPoints,
          ),
          earned: [
            'SPONSOR_SUBSCRIPTION',
            'INVESTOR_SUBSCRIPTION',
            'CAMPAIGN_LISTING_FEE',
            'CAMPAIGN_LISTING_MARKETING_RESOURCE_FEE',
            'CAMPAIGN_LISTING_SALES_RESOURCE_FEE',
          ].includes(transaction?.revenueType)
            ? formatCurrency(
                transaction?.totalAmount ?? 0,
                generalSettings.currencySymbolSide,
                transaction?.currencySymbol,
                transaction?.currencyCode,
                generalSettings.decimalPoints,
              )
            : transaction?.revenueType === 'INVESTMENT_FEE'
              ? formatCurrency(
                  transaction?.investmentFees?.amount ?? 0,
                  generalSettings.currencySymbolSide,
                  transaction?.currencySymbol,
                  transaction?.currencyCode,
                  generalSettings.decimalPoints,
                )
              : formatCurrency(
                  transaction?.earnedFee ?? 0,
                  generalSettings.currencySymbolSide,
                  transaction?.currencySymbol,
                  transaction?.currencyCode,
                  generalSettings.decimalPoints,
                ),
          createdAt: transaction?.createdAt
            ? `${moment(transaction?.createdAt).format(
                generalSettings.dateFormat,
              )} ${new Date(transaction.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}`
            : '-',
          gatewayId:
            transaction?.doneFromWallet === 'no'
              ? transaction?.paymentGatewayTitle.toUpperCase()
              : transaction?.doneFromWallet === 'full'
                ? 'WALLET'
                : transaction?.doneFromWallet === 'partial'
                  ? `WALLET + ${transaction?.paymentGatewayTitle.toUpperCase()}`
                  : '-',

          status: statusType(transaction?.status || ''),
        }))
      : [];

  return (
    <>
      <FormProvider {...methods}>
        <Form
          className="my-3"
          name="searchTransaction"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <SearchFilters
            revenueTypes={REVENUE_TYPES}
            statusFilterList={STATUS_FILTERS}
            resetFilters={resetFilters}
          />
        </Form>
      </FormProvider>
      <Accordion
        defaultActiveKey="1"
        className="mb-5"
      >
        <Accordion.Item>
          <Accordion.Header className="px-3 my-2 custom-accordian-header">
            <h5 className="w-100 position-absolute start-0 rounded-fix info-bg-clr text-center text-black font-xl fw-medium py-2 px-3 mb-0">
              Total Earned Revenue:{' '}
              <span className="fw-bolder text-info">
                {formatCurrency(
                  revenueTransactionData?.totals?.totalEarnedRevenue ?? 0,
                  generalSettings?.currencySymbolSide,
                  generalSettings?.currencyId?.symbol,
                  generalSettings?.currencyId?.code,
                  generalSettings?.decimalPoints,
                )}
              </span>
            </h5>
          </Accordion.Header>
          <Accordion.Body className="row pt-0">
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix fw-medium text-center h-100">
                <span className="text-black d-block">
                  Total Investment Fees
                </span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.totalInvestmentFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">Project Listing Fees</span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.projectListingFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">
                  Project Liquidation Fees
                </span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.totalLiquidationFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">Project Closing Fees</span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.projectClosingFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">
                  Investor Account Fees
                </span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.investorAccountFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">
                  Marketing Resource Allocation Fees
                </span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.marketingResourceFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">
                  Total Operations Fees
                </span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.totalOperationsFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mt-5">
              <div className="info-bg-clr px-2 py-4 rounded-fix text-center h-100 fw-medium">
                <span className="text-black d-block">
                  Sales Resource Allocation Fees
                </span>
                <strong className="d-block text-info fw-bolder font-size-lg">
                  {formatCurrency(
                    revenueTransactionData?.totals?.salesResourceFee ?? 0,
                    generalSettings?.currencySymbolSide,
                    generalSettings?.currencyId?.symbol,
                    generalSettings?.currencyId?.code,
                    generalSettings?.decimalPoints,
                  )}
                </strong>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Card>
        <CardHeader title="Revenue Transactions">
          {currentPermissions?.export_revenue_transactions && (
            <CardHeaderToolbar>
              <CSVLink
                data={exportTransactionList}
                headers={transactionCSVHeaders}
                className="btn font-size-sm btn-secondary fw-bold text-white py-1-5 me-3"
                disabled={exportLoading}
                filename={'Revenue Transaction List.csv'}
              >
                <SVG
                  height={15}
                  className="me-2"
                  width={15}
                  src={toAbsoluteUrl('media/svg/icons/General/export-csv.svg')}
                />
                {exportLoading ? 'Loading..' : 'Export CSV'}
              </CSVLink>
            </CardHeaderToolbar>
          )}
        </CardHeader>
        <CardBody className="pt-0 px-4 py-6">
          <div className="sticky-table-header-wrapper">
            <Table className="table-head-custom table-custom-bg table-vertical-center">
              <thead>
                <tr>
                  <th>
                    <SortDropdownHeader
                      columnKey="userName"
                      onSort={handleSort}
                      currentSort={sortConfig}
                    >
                      User Name
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="revenueType"
                      onSort={handleSort}
                      currentSort={sortConfig}
                    >
                      Revenue Type
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="transactionId"
                      onSort={handleSort}
                      currentSort={sortConfig}
                    >
                      Transaction ID
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="amount"
                      onSort={handleSort}
                      currentSort={sortConfig}
                    >
                      Amount
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="earned"
                      onSort={handleSort}
                      currentSort={sortConfig}
                    >
                      Earned
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="createdAt"
                      onSort={handleSort}
                      currentSort={sortConfig}
                    >
                      Date & Time
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="gatewayId"
                      onSort={handleSort}
                      align="right"
                      currentSort={sortConfig}
                    >
                      Payment Method
                    </SortDropdownHeader>
                  </th>
                  <th>
                    <SortDropdownHeader
                      columnKey="preapprovalStatus"
                      onSort={handleSort}
                      align="right"
                      currentSort={sortConfig}
                    >
                      Status
                    </SortDropdownHeader>
                  </th>
                </tr>
              </thead>
              <tbody>{transactionList}</tbody>
            </Table>
            {displayLoadMore ? (
              <div className="mt-5 text-center">
                <Button
                  type="button"
                  variant="primary"
                  className="btn btn-dark py-2"
                  onClick={() => loadMore()}
                  disabled={isValidating}
                >
                  {isValidating ? (
                    <Spinner
                      variant="light"
                      style={{ height: '1.2rem', width: '1.2rem' }}
                    />
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>
      <RefundRejectModal
        showRefundModal={showRefundCancelModal}
        handleCloseRefundModal={handleCloseRefundModal}
        refundProcessTrigger={refundProcessTrigger}
        revenueTransactionDataMutate={revenueTransactionDataMutate}
        transactionId={transactionId}
      />
    </>
  );
};
export default compose(withTranslation())(Transactions);
